import type { GlobalDialogProps } from "@/components/global-dialog/global-dialog";
import { ImpressaoSheetState } from "@/interfaces/impressao/impressao.interface";
import { CaronaDto } from "@/interfaces/carona.interface";
import { CirculoDto } from "@/interfaces/circulo.intefarce";
import {
  CamposConfiguracaoFormSchema,
  EquipesConfiguracaoFormSchema,
  FormularioConfiguracaoFormSchema,
  IntegracoesConfiguracaoFormSchema,
  MensagensConfiguracaoFormSchema,
  MidiasConfiguracaoFormSchema,
} from "@/interfaces/configuracao.interface";
import { CredencialFormSchema } from "@/interfaces/credencial.interface";
import {
  AdicionalFormSchema,
  EventoDto,
  EventoBackgroundFormSchema,
  EventoInformativoFormSchema,
} from "@/interfaces/evento.interface";
import { PequenosGruposDto } from "@/interfaces/pequenos-grupos.interface";
import { DisciplinasDto } from "@/interfaces/disciplinas.interface";
import { LancamentoFormSchema } from "@/interfaces/lancamentos.interface";
import { MarcadorDto } from "@/interfaces/marcador.interface";
import { PadrinhoDto } from "@/interfaces/padrinho.interface";
import { ProdutoDto } from "@/interfaces/produto.interface";
import { QuartoDto } from "@/interfaces/quarto.interface";
import { ReuniaoFormSchema } from "@/interfaces/reuniao.interface";
import {
  UsuarioInviteFormSchema,
  UsuarioPermissao,
} from "@/interfaces/usuarios.interface";
import { Completecirculos, mensagemsModel } from "@/interfaces/zod";
import { PayloadAction, createSlice, createSelector } from "@reduxjs/toolkit";
import { z } from "zod";
import { SetEventoId } from "./eventos.slice";
import { LocalDto } from "@/interfaces/local.interface";
import { AgendaDto, PerguntarIAResponse, AgendaIAStreamState } from "@/interfaces/agendamento.interface";
import { PerguntarIAEventoResponse, EventoIAStreamState } from "@/interfaces/evento.interface";
import { MinisterioFormSchema } from "@/interfaces/ministerio.interface";
import {
  TipoAgendaFormSchema,
  AgendaFuncaoFormSchema,
  TipoAgendaCamposFormSchema,
} from "@/interfaces/zod/agendamento";
import { createAction } from "@reduxjs/toolkit";
import type { RootState } from "@/redux/store";
import type { MensagensInfantilContexto } from "@/pages/Infantil/utils/infantil-mensagens-variaveis";
import { CampoPersonalizadoSchema } from "@/interfaces/campo-personalizado.interface";
import {
  LinkPageSchema,
  LinkSchema,
  LinkTemplateSchema,
} from "@/interfaces/zod/links";
import { ICurso } from "@/interfaces/curso.interface";
import { EquipeFormSchema } from "@/interfaces/equipe.interface";
import { SemestreLetivoFormSchema } from "@/interfaces/semestreletivo.interface";

export interface FormMethods {
  reset: (values?: any) => void;
  setValue: (name: string, value: any) => void;
  getValues: () => any;
  handleSubmit: (onSubmit: (data: any) => void) => (e?: any) => void;
  formState: {
    isDirty: boolean;
    isSubmitting: boolean;
    errors: Record<string, any>;
    dirtyFields: Record<string, boolean>;
  };
}

/** Seção do painel do evento aberta ao chegar pelo menu ⋮ (ids das etapas do assistente + extras da edição). */
export type PainelEventoSecao =
  | "modalidade"
  | "evento"
  | "eventoPublico"
  | "formulario"
  | "inscricoes"
  | "equipes"
  | "comunicacao"
  | "status";

export type PainelEventoContext = {
  eventoId: number;
  secao?: PainelEventoSecao | null;
};

export interface ISheetState {
  ProfileCardParticipanteId: null | number;
  /** EventoId da turma quando o perfil foi aberto a partir de uma turma (ex: listagem geral alunos) */
  ProfileCardParticipanteEventoId: null | number;
  ProfileCardEquipanteId: null | number;
  /** EventoId ao abrir o perfil de um voluntário a partir do evento (busca, listagem) */
  ProfileCardEquipanteEventoId: null | number;
  /** Ministério de origem ao abrir o perfil de um membro do hub */
  ProfileCardMinisterioId: null | number;
  ProfileCardPedidoOracaoId: null | number;
  ProfileCardAgendaId: null | number;
  CheckinCasalParticipanteId: null | number;
  CheckinParticipanteId: null | number;
  CheckinEquipanteId: null | number;
  CheckinCasaslEquipanteId: null | number;
  ProfileCardAccordionOpen: null | string;
  PagamentosParticipanteId: null | number;
  PagamentosEquipanteId: null | number;
  PagamentosLoginId: null | number;
  LancamentosAbertoEmail: null | string;
  PagamentosRealizadosEmail: null | string;
  TurmasEmail: null | string;
  TurmasTipo: null | "professor" | "aluno";
  TurmasPassadas: boolean;
  DetalheTurmaId: null | number;
  DetalheTurmaTipo: null | "professor" | "aluno";
  MensagensEquipanteId: null | number;
  MensagensParticipanteId: null | number;
  /** Contexto ao enviar mensagem para responsável a partir do perfil infantil. */
  MensagensInfantilContexto: MensagensInfantilContexto | null;
  FormParticipanteId: null | number;
  FormEquipanteId: null | number;
  FormEquipanteContext: { origem?: string } | null;
  FormAdicionalValues: z.infer<typeof AdicionalFormSchema>;
  FormCupomValues: Record<string, unknown> | null;
  AnexosLancamentoId: null | number;
  AnexosParticipanteId: null | number;
  AnexosEquipeId: null | number;
  AnexosEquipanteId: null | number;
  AnexosTarefaId: null | number;
  MovimentacaoProdutoId: null | number;
  MembrosEquipeId: null | number;
  /** Equipe a reabrir quando form equipante fechar (fluxo membros → cadastro → fechar) */
  PendingMembrosEquipeIdReopen: null | number;
  AdicionaisEventoId: null | number;
  CupomUtilizacoesContext: {
    EventoId: number;
    CupomId: number;
    LoginId: number;
    Codigo?: string;
    TipoPublico?: number;
    QuantidadeUtilizada?: number;
    LimiteUso?: number | null;
  } | null;
  HistoricoContext: import("@/interfaces/historico.interface").HistoricoContextState | null;
  WhatsappConnectOpen: boolean;
  CallbackWhatsapp: () => void | null;
  FormProdutoValues: ProdutoDto;
  FormCirculoValues: CirculoDto;
  FormCaronaValues: CaronaDto;
  FormPadrinhoValues: PadrinhoDto;
  DirigentesCirculoId: null | number;
  ProfessoresTurmaId: null | number;
  FormQuartoValues: QuartoDto;
  FormEventoValues: EventoDto;
  /** Painel de edição do evento publicado (cards por seção, mesmo visual do assistente). */
  PainelEventoContext: PainelEventoContext | null;
  FormPequenosGruposValues: PequenosGruposDto;
  FormDisciplinasValues: DisciplinasDto;
  FormTurmaValues: EventoDto;
  FormReuniaoValues: z.infer<typeof ReuniaoFormSchema>;
  FormPagamentoValues: z.infer<typeof LancamentoFormSchema>;
  CredencialPessoa: any;
  ShareEventoValues: EventoDto;
  ShareCursoValues: ICurso;
  SharePequenosGruposValues: PequenosGruposDto;
  ShareInformativoValues: z.infer<typeof EventoInformativoFormSchema> | null;
  FormMarcadorValues: MarcadorDto;
  FormLocalValues: LocalDto;
  FormSemestreLetivoValues: z.infer<typeof SemestreLetivoFormSchema> | null;
  FormEquipeValues: z.infer<typeof EquipeFormSchema> | null;
  FormAgendaValues: AgendaDto;
  FormInformativoValues: z.infer<typeof EventoInformativoFormSchema> | null;
  FormMensagemValues: z.infer<typeof mensagemsModel>;
  FormInviteValues: z.infer<typeof UsuarioInviteFormSchema>;
  ConviteResponsavelSalinhaContext: {
    loginId: number;
    eventoId: number;
    circuloId: number;
    tituloSalinha?: string;
  } | null;
  FormPermissaoValues: z.infer<typeof UsuarioPermissao>;
  GlobalDialog: GlobalDialogProps;
  ImpressaoSheet: ImpressaoSheetState;
  PageTitle: string;
  hideEvento: boolean;
  FormMidiaValues: z.infer<typeof MidiasConfiguracaoFormSchema>;
  FormEquipesValues: z.infer<typeof EquipesConfiguracaoFormSchema>;
  FormMensagensValues: z.infer<typeof MensagensConfiguracaoFormSchema>;
  FormCamposValues: z.infer<typeof CamposConfiguracaoFormSchema>;
  FormFormularioValues: z.infer<typeof FormularioConfiguracaoFormSchema>;
  IntegracoesConfiguracaoValues: z.infer<typeof IntegracoesConfiguracaoFormSchema> | null;
  FormCamposPersonalizadosValues: z.infer<typeof CampoPersonalizadoSchema>;
  FormCredencialValues: z.infer<typeof CredencialFormSchema>;
  FormMinisterioValues: z.infer<typeof MinisterioFormSchema> | null;
  FormLinkPageValues: z.infer<typeof LinkPageSchema> | null;
  FormLinkValues: z.infer<typeof LinkSchema> | null;
  FormLinkTemplateValues: z.infer<typeof LinkTemplateSchema> | null;
  FormProvider: any;
  SelectedVenda: any;
  FormEventoBackgroundValues: z.infer<typeof EventoBackgroundFormSchema> | null;
  FormJornadaValues: any;
  SelectedTarefa: any;
  AvaliacaoViewValues: any;
  AvaliacaoSheetValues: any;
  FormGrupoDevocionalValues: any;
  FormCheckinValues: any;
  FormEtiquetaDevocionalValues: any;
  SelectedCheckinId: number | null;
  SelectedGrupoDevocionalId: number | null;
  Retrospectiva2025Open: boolean;
  FormTipoAgendaValues: z.infer<typeof TipoAgendaFormSchema> | null;
  FormTipoAgendaCamposValues: z.infer<typeof TipoAgendaCamposFormSchema> | null;
  FormAgendaFuncaoValues: z.infer<typeof AgendaFuncaoFormSchema> | null;
  SelectedAgendaId: number | null;
  AgendaEscalasOpen: boolean;
  GuiaUsuariosAgendaOpen: boolean;
  FormGuiaUsuariosAgendaValues: { Id: string; UserName?: string; equipantes?: { Nome?: string } } | null;
  AgendaInformacoesAdicionaisOpen: boolean;
  IncluirVoluntarioOpen: boolean;
  AssistenteMontagemOpen: boolean;
  ConfigMontagemEquipesOpen: boolean;
  AgendaIAResponseValues: PerguntarIAResponse | null;
  EventoIAResponseValues: PerguntarIAEventoResponse | null;
  EventoIAStreamState: EventoIAStreamState | null;
  AgendaIAStreamState: AgendaIAStreamState | null;
  /** Quando true, Sheets devem ignorar o próximo outside click (evita fechar sheet embaixo ao fechar detalhe da turma) */
  SheetSuppressOutsideClick: boolean;
}

const initialState: ISheetState = {
  AnexosEquipanteId: null,
  AnexosTarefaId: null,
  PagamentosEquipanteId: null,
  MensagensEquipanteId: null,
  MensagensInfantilContexto: null,
  FormMensagemValues: null,
  FormCamposPersonalizadosValues: null,
  FormCamposValues: null,
  FormFormularioValues: null,
  IntegracoesConfiguracaoValues: null,
  ProfileCardParticipanteId: null,
  ProfileCardParticipanteEventoId: null,
  FormAdicionalValues: null,
  FormCupomValues: null,
  ProfileCardEquipanteId: null,
  ProfileCardEquipanteEventoId: null,
  ProfileCardMinisterioId: null,
  ProfileCardPedidoOracaoId: null,
  ProfileCardAgendaId: null,
  CheckinParticipanteId: null,
  AnexosEquipeId: null,
  CheckinCasalParticipanteId: null,
  FormPermissaoValues: null,
  FormMensagensValues: null,
  CheckinCasaslEquipanteId: null,
  CheckinEquipanteId: null,
  DirigentesCirculoId: null,
  ProfessoresTurmaId: null,
  MovimentacaoProdutoId: null,
  ProfileCardAccordionOpen: null,
  PagamentosParticipanteId: null,
  PagamentosLoginId: null,
  LancamentosAbertoEmail: null,
  PagamentosRealizadosEmail: null,
  TurmasEmail: null,
  TurmasTipo: null,
  TurmasPassadas: false,
  DetalheTurmaId: null,
  DetalheTurmaTipo: null,
  MensagensParticipanteId: null,
  CredencialPessoa: null,
  MembrosEquipeId: null,
  PendingMembrosEquipeIdReopen: null,
  AdicionaisEventoId: null,
  CupomUtilizacoesContext: null,
  FormReuniaoValues: null,
  AnexosLancamentoId: null,
  AnexosParticipanteId: null,
  HistoricoContext: null,
  WhatsappConnectOpen: false,
  CallbackWhatsapp: null,
  FormCirculoValues: null,
  FormProdutoValues: null,
  GlobalDialog: null,
  ImpressaoSheet: {
    isOpen: false,
    processarImpressao: async () => { },
    ids: [],
    camposDisponiveis: { participantes: [] },
    moduloNome: "",
    colunasPadrao: { participantes: [] },
    nomeEquipeCirculo: undefined,
  },
  PageTitle: "SysIgreja",
  FormCaronaValues: null,
  FormPagamentoValues: null,
  FormInviteValues: null,
  ConviteResponsavelSalinhaContext: null,
  FormPadrinhoValues: null,
  FormQuartoValues: null,
  FormParticipanteId: null,
  FormEquipanteId: null,
  FormEquipanteContext: null,
  FormEventoValues: null,
  PainelEventoContext: null,
  FormPequenosGruposValues: null,
  FormDisciplinasValues: null,
  FormTurmaValues: null,
  ShareEventoValues: null,
  ShareCursoValues: null,
  SharePequenosGruposValues: null,
  ShareInformativoValues: null,
  FormLocalValues: null,
  FormSemestreLetivoValues: null,
  FormEquipeValues: null,
  FormAgendaValues: null,
  FormInformativoValues: null,
  FormMarcadorValues: null,
  FormMidiaValues: null,
  FormEquipesValues: null,
  FormCredencialValues: null,
  FormMinisterioValues: null,
  hideEvento: false,
  FormProvider: null,
  SelectedVenda: null,
  FormLinkPageValues: null,
  FormLinkValues: null,
  FormLinkTemplateValues: null,
  FormEventoBackgroundValues: null,
  FormJornadaValues: null,
  SelectedTarefa: null,
  AvaliacaoViewValues: null,
  AvaliacaoSheetValues: null,
  FormGrupoDevocionalValues: null,
  FormCheckinValues: null,
  FormEtiquetaDevocionalValues: null,
  SelectedCheckinId: null,
  SelectedGrupoDevocionalId: null,
  Retrospectiva2025Open: false,
  FormTipoAgendaValues: null,
  FormTipoAgendaCamposValues: null,
  FormAgendaFuncaoValues: null,
  SelectedAgendaId: null,
  AgendaEscalasOpen: false,
  GuiaUsuariosAgendaOpen: false,
  FormGuiaUsuariosAgendaValues: null,
  AgendaInformacoesAdicionaisOpen: false,
  IncluirVoluntarioOpen: false,
  AssistenteMontagemOpen: false,
  ConfigMontagemEquipesOpen: false,
  AgendaIAResponseValues: null,
  EventoIAResponseValues: null,
  EventoIAStreamState: null,
  AgendaIAStreamState: null,
  SheetSuppressOutsideClick: false,
};

export const sheetSlice = createSlice({
  name: "profileCard",
  initialState,
  reducers: {
    SetProfileCardParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardParticipanteId = payload;
      if (payload === null) {
        state.ProfileCardParticipanteEventoId = null;
      }
    },
    SetProfileCardParticipanteEventoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardParticipanteEventoId = payload;
    },
    SetProfileCardEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardEquipanteId = payload;
      if (payload === null) {
        state.ProfileCardMinisterioId = null;
        state.ProfileCardEquipanteEventoId = null;
      }
    },
    SetProfileCardEquipanteEventoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardEquipanteEventoId = payload;
    },
    SetProfileCardMinisterioId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardMinisterioId = payload;
    },
    SetProfileCardPedidoOracaoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardPedidoOracaoId = payload;
    },
    SetProfileCardAgendaId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfileCardAgendaId = payload;
    },
    SetPageTitle(state: ISheetState, { payload }: PayloadAction<string>) {
      state.PageTitle = payload;
    },
    SetProfileCardAccordionOpen(
      state: ISheetState,
      { payload }: PayloadAction<string | null>
    ) {
      state.ProfileCardAccordionOpen = payload;
    },
    SetPagamentosParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.PagamentosParticipanteId = payload;
    },
    SetPagamentosEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.PagamentosEquipanteId = payload;
    },

    SetPagamentosLoginId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.PagamentosLoginId = payload;
    },
    SetLancamentosAbertoEmail(
      state: ISheetState,
      { payload }: PayloadAction<string | null>
    ) {
      state.LancamentosAbertoEmail = payload;
    },
    SetPagamentosRealizadosEmail(
      state: ISheetState,
      { payload }: PayloadAction<string | null>
    ) {
      state.PagamentosRealizadosEmail = payload;
    },
    SetTurmasEmail(
      state: ISheetState,
      { payload }: PayloadAction<string | null>
    ) {
      state.TurmasEmail = payload;
      state.SheetSuppressOutsideClick = payload !== null || state.DetalheTurmaId !== null;
    },
    SetTurmasTipo(
      state: ISheetState,
      { payload }: PayloadAction<"professor" | "aluno" | null>
    ) {
      state.TurmasTipo = payload;
    },
    SetTurmasPassadas(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.TurmasPassadas = payload;
    },
    SetDetalheTurmaId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.DetalheTurmaId = payload;
      state.SheetSuppressOutsideClick = state.TurmasEmail !== null || payload !== null;
    },
    SetDetalheTurmaTipo(
      state: ISheetState,
      { payload }: PayloadAction<"professor" | "aluno" | null>
    ) {
      state.DetalheTurmaTipo = payload;
    },
    SetMensagensEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.MensagensEquipanteId = payload;
      if (payload == null) {
        state.MensagensInfantilContexto = null;
      }
    },
    SetMensagensInfantilContexto(
      state: ISheetState,
      { payload }: PayloadAction<MensagensInfantilContexto | null>,
    ) {
      state.MensagensInfantilContexto = payload;
    },
    SetAnexosEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AnexosEquipanteId = payload;
    },
    SetMembrosEquipeId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.MembrosEquipeId = payload;
    },
    SetAdicionaisEventoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AdicionaisEventoId = payload;
    },
    SetCupomUtilizacoesContext(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<ISheetState["CupomUtilizacoesContext"]>
    ) {
      state.CupomUtilizacoesContext = payload;
    },
    SetAnexosEquipeId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AnexosEquipeId = payload;
    },
    SetMovimentacaoProdutoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.MovimentacaoProdutoId = payload;
    },
    SetAnexosLancamentoId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AnexosLancamentoId = payload;
    },
    SetAnexosParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AnexosParticipanteId = payload;
    },
    SetAnexosTarefaId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.AnexosTarefaId = payload;
    },
    SetMensagensParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.MensagensParticipanteId = payload;
      if (payload == null && state.MensagensEquipanteId == null) {
        state.MensagensInfantilContexto = null;
      }
    },
    SetHistoricoContext(
      state: ISheetState,
      { payload }: PayloadAction<import("@/interfaces/historico.interface").HistoricoContextState | null>,
    ) {
      state.HistoricoContext = payload;
    },
    SetCallbackWhatsapp(
      state: ISheetState,
      { payload }: PayloadAction<() => void | null>
    ) {
      state.CallbackWhatsapp = payload;
    },
    SetWhatsappConnectOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.WhatsappConnectOpen = payload;
    },
    SetFormCirculoValues(
      state: ISheetState,
      { payload }: PayloadAction<Completecirculos>
    ) {
      state.FormCirculoValues = payload;
    },
    SetFormCaronaValues(
      state: ISheetState,
      { payload }: PayloadAction<CaronaDto>
    ) {
      state.FormCaronaValues = payload;
    },
    SetFormReuniaoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof ReuniaoFormSchema>>
    ) {
      state.FormReuniaoValues = payload;
    },
    SetFormCamposValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof CamposConfiguracaoFormSchema>>
    ) {
      state.FormCamposValues = payload;
    },
    SetFormCamposPersonalizadosValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof CampoPersonalizadoSchema>>
    ) {
      state.FormCamposPersonalizadosValues = payload;
    },
    SetFormMensagensValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<z.infer<typeof MensagensConfiguracaoFormSchema>>
    ) {
      state.FormMensagensValues = payload;
    },
    SetFormFormularioValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<z.infer<typeof FormularioConfiguracaoFormSchema>>
    ) {
      state.FormFormularioValues = payload;
    },
    SetIntegracoesConfiguracaoValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<z.infer<typeof IntegracoesConfiguracaoFormSchema> | null>
    ) {
      state.IntegracoesConfiguracaoValues = payload;
    },
    SetFormProdutoValues(
      state: ISheetState,
      { payload }: PayloadAction<ProdutoDto>
    ) {
      state.FormProdutoValues = payload;
    },
    SetFormCredencialValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof CredencialFormSchema>>
    ) {
      state.FormCredencialValues = payload;
    },
    SetFormPagamentoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof LancamentoFormSchema>>
    ) {
      state.FormPagamentoValues = payload;
    },
    SetFormPadrinhoValues(
      state: ISheetState,
      { payload }: PayloadAction<PadrinhoDto>
    ) {
      state.FormPadrinhoValues = payload;
    },
    SetDirigentesCirculoId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.DirigentesCirculoId = payload;
    },
    SetProfessoresTurmaId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.ProfessoresTurmaId = payload;
    },
    SetFormQuartoValues(
      state: ISheetState,
      { payload }: PayloadAction<QuartoDto>
    ) {
      state.FormQuartoValues = payload;
    },
    SetFormParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.FormParticipanteId = payload;
      // [PERF_OPTIMIZE] Fechar profile card quando form abre para liberar recursos
      if (payload !== null) {
        state.ProfileCardParticipanteId = null;
        state.ProfileCardParticipanteEventoId = null;
        state.ProfileCardAccordionOpen = null;
      }
    },
    SetFormEquipanteId(state: ISheetState, { payload }: PayloadAction<number | null>) {
      state.FormEquipanteId = payload;
      if (payload !== null) {
        state.ProfileCardEquipanteId = null;
        state.ProfileCardEquipanteEventoId = null;
        state.ProfileCardMinisterioId = null;
        state.ProfileCardAccordionOpen = null;
        // Fechar membros ao abrir form (evita Radix "Maximum update depth"). Guardar para reabrir ao fechar.
        if (state.MembrosEquipeId !== null) {
          state.PendingMembrosEquipeIdReopen = state.MembrosEquipeId;
          state.MembrosEquipeId = null;
        }
      } else {
        // Ao fechar form: reabrir membros se veio do fluxo equipes
        if (state.PendingMembrosEquipeIdReopen !== null) {
          state.MembrosEquipeId = state.PendingMembrosEquipeIdReopen;
          state.PendingMembrosEquipeIdReopen = null;
        }
        // Sempre limpar contexto quando o form é fechado
        state.FormEquipanteContext = null;
      }
    },
    SetFormEquipanteContext(
      state: ISheetState,
      { payload }: PayloadAction<{ origem?: string } | null>,
    ) {
      state.FormEquipanteContext = payload;
    },
    SetFormAdicionalValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof AdicionalFormSchema>>
    ) {
      state.FormAdicionalValues = payload;
    },
    SetFormCupomValues(
      state: ISheetState,
      { payload }: PayloadAction<Record<string, unknown> | null>
    ) {
      state.FormCupomValues = payload;
    },
    SetCheckinParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.CheckinParticipanteId = payload;
      state.CheckinEquipanteId = null;
      state.CheckinCasaslEquipanteId = null;
    },
    SetCheckinCasalParticipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.CheckinCasalParticipanteId = payload;
      state.CheckinCasaslEquipanteId = null;
      state.CheckinEquipanteId = null;
    },
    SetCheckinCasaslEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.CheckinCasaslEquipanteId = payload;
      state.CheckinCasalParticipanteId = null;
      state.CheckinParticipanteId = null;
    },
    SetCheckinEquipanteId(
      state: ISheetState,
      { payload }: PayloadAction<number>
    ) {
      state.CheckinEquipanteId = payload;
      state.CheckinParticipanteId = null;
      state.CheckinCasalParticipanteId = null;
    },
    SetFormEventoValues(
      state: ISheetState,
      { payload }: PayloadAction<EventoDto>
    ) {
      state.FormEventoValues = payload;
    },
    SetPainelEventoContext(
      state: ISheetState,
      { payload }: PayloadAction<PainelEventoContext | null>
    ) {
      state.PainelEventoContext = payload;
    },
    SetFormPequenosGruposValues(
      state: ISheetState,
      { payload }: PayloadAction<PequenosGruposDto>
    ) {
      state.FormPequenosGruposValues = payload;
    },
    SetFormDisciplinasValues(
      state: ISheetState,
      { payload }: PayloadAction<DisciplinasDto>
    ) {
      state.FormDisciplinasValues = payload;
    },
    SetFormTurmaValues(
      state: ISheetState,
      { payload }: PayloadAction<EventoDto>
    ) {
      state.FormTurmaValues = payload;
    },
    SetShareEventoValues(
      state: ISheetState,
      { payload }: PayloadAction<EventoDto>
    ) {
      state.ShareEventoValues = payload;
    },
    SetShareCursoValues(
      state: ISheetState,
      { payload }: PayloadAction<ICurso>
    ) {
      state.ShareCursoValues = payload;
    },
    SetSharePequenosGruposValues(
      state: ISheetState,
      { payload }: PayloadAction<PequenosGruposDto>
    ) {
      state.SharePequenosGruposValues = payload;
    },
    SetShareInformativoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof EventoInformativoFormSchema> | null>
    ) {
      state.ShareInformativoValues = payload;
    },
    SetFormMarcadorValues(
      state: ISheetState,
      { payload }: PayloadAction<MarcadorDto>
    ) {
      state.FormMarcadorValues = payload;
    },
    SetFormLocalValues(
      state: ISheetState,
      { payload }: PayloadAction<LocalDto>
    ) {
      state.FormLocalValues = payload;
    },
    SetFormSemestreLetivoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof SemestreLetivoFormSchema> | null>
    ) {
      state.FormSemestreLetivoValues = payload;
    },
    SetFormEquipeValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof EquipeFormSchema> | null>
    ) {
      state.FormEquipeValues = payload;
    },
    SetFormAgendaValues(
      state: ISheetState,
      { payload }: PayloadAction<LocalDto>
    ) {
      state.FormAgendaValues = payload;
    },
    SetFormInformativoValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<z.infer<typeof EventoInformativoFormSchema> | null>
    ) {
      state.FormInformativoValues = payload;
    },
    SetFormMensagemValues(
      state: ISheetState,
      { payload }: PayloadAction<MarcadorDto>
    ) {
      state.FormMensagemValues = payload;
    },
    SetFormFormMidiaValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof MidiasConfiguracaoFormSchema>>
    ) {
      state.FormMidiaValues = payload;
    },
    SetFormEquipesValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof EquipesConfiguracaoFormSchema>>
    ) {
      state.FormEquipesValues = payload;
    },
    SetFormInviteValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof UsuarioInviteFormSchema>>
    ) {
      state.FormInviteValues = payload;
    },
    SetConviteResponsavelSalinhaContext(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<ISheetState["ConviteResponsavelSalinhaContext"]>
    ) {
      state.ConviteResponsavelSalinhaContext = payload;
    },
    SetFormPermissaoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof UsuarioPermissao>>
    ) {
      state.FormPermissaoValues = payload;
    },
    SetCredencialPessoa(state: ISheetState, { payload }: PayloadAction<any>) {
      state.CredencialPessoa = payload;
    },
    SetGlobalDialogProps(
      state: ISheetState,
      { payload }: PayloadAction<GlobalDialogProps>
    ) {
      state.GlobalDialog = payload;
    },
    SetSheetSuppressOutsideClick(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.SheetSuppressOutsideClick = payload;
    },
    SetFormMinisterioValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof MinisterioFormSchema> | null>
    ) {
      state.FormMinisterioValues = payload;
    },
    SetHideEvento(state: ISheetState, { payload }: PayloadAction<boolean>) {
      state.hideEvento = payload;
    },
    SetFormProvider(state: ISheetState, { payload }: PayloadAction<any>) {
      state.FormProvider = payload
        ? {
          formState: {
            dirtyFields: { ...payload.formState.dirtyFields },
          },
        }
        : null;
    },
    setSelectedVenda(state: ISheetState, { payload }: PayloadAction<any>) {
      state.SelectedVenda = payload;
    },
    setSelectedTarefa(state: ISheetState, { payload }: PayloadAction<any>) {
      state.SelectedTarefa = payload;
    },
    SetFormLinkPageValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof LinkPageSchema> | null>
    ) {
      state.FormLinkPageValues = payload;
    },
    SetFormLinkValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof LinkSchema> | null>
    ) {
      state.FormLinkValues = payload;
    },
    SetFormLinkTemplateValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof LinkTemplateSchema> | null>
    ) {
      state.FormLinkTemplateValues = payload;
    },
    SetFormEventoBackgroundValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<z.infer<typeof EventoBackgroundFormSchema> | null>
    ) {
      state.FormEventoBackgroundValues = payload;
    },
    SetAvaliacaoViewValues(
      state: ISheetState,
      { payload }: PayloadAction<any>
    ) {
      state.AvaliacaoViewValues = payload;
    },
    SetAvaliacaoSheetValues(
      state: ISheetState,
      { payload }: PayloadAction<any>
    ) {
      state.AvaliacaoSheetValues = payload;
    },
    SetFormJornadaValues(state: ISheetState, { payload }: PayloadAction<any>) {
      state.FormJornadaValues = payload;
    },
    SetFormGrupoDevocionalValues(
      state: ISheetState,
      { payload }: PayloadAction<any>
    ) {
      state.FormGrupoDevocionalValues = payload;
    },
    SetFormCheckinValues(state: ISheetState, { payload }: PayloadAction<any>) {
      state.FormCheckinValues = payload;
    },
    SetFormEtiquetaDevocionalValues(
      state: ISheetState,
      { payload }: PayloadAction<any>
    ) {
      state.FormEtiquetaDevocionalValues = payload;
    },
    SetSelectedCheckinId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.SelectedCheckinId = payload;
    },
    SetSelectedGrupoDevocionalId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.SelectedGrupoDevocionalId = payload;
    },
    SetImpressaoSheet(
      state: ISheetState,
      { payload }: PayloadAction<ImpressaoSheetState>
    ) {
      state.ImpressaoSheet = payload;
    },
    SetRetrospectiva2025Open(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.Retrospectiva2025Open = payload;
    },
    SetFormTipoAgendaValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof TipoAgendaFormSchema> | null>
    ) {
      state.FormTipoAgendaValues = payload;
    },
    SetFormTipoAgendaCamposValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof TipoAgendaCamposFormSchema> | null>
    ) {
      state.FormTipoAgendaCamposValues = payload;
    },
    SetFormAgendaFuncaoValues(
      state: ISheetState,
      { payload }: PayloadAction<z.infer<typeof AgendaFuncaoFormSchema> | null>
    ) {
      state.FormAgendaFuncaoValues = payload;
    },
    SetSelectedAgendaId(
      state: ISheetState,
      { payload }: PayloadAction<number | null>
    ) {
      state.SelectedAgendaId = payload;
    },
    SetAgendaEscalasOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.AgendaEscalasOpen = payload;
    },
    SetGuiaUsuariosAgendaOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.GuiaUsuariosAgendaOpen = payload;
    },
    SetFormGuiaUsuariosAgendaValues(
      state: ISheetState,
      {
        payload,
      }: PayloadAction<{ Id: string; UserName?: string; equipantes?: { Nome?: string } } | null>
    ) {
      state.FormGuiaUsuariosAgendaValues = payload;
    },
    SetAgendaInformacoesAdicionaisOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.AgendaInformacoesAdicionaisOpen = payload;
    },
    SetIncluirVoluntarioOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.IncluirVoluntarioOpen = payload;
    },
    SetAssistenteMontagemOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.AssistenteMontagemOpen = payload;
    },
    SetConfigMontagemEquipesOpen(
      state: ISheetState,
      { payload }: PayloadAction<boolean>
    ) {
      state.ConfigMontagemEquipesOpen = payload;
    },
    SetAgendaIAResponseValues(
      state: ISheetState,
      { payload }: PayloadAction<PerguntarIAResponse | null>
    ) {
      state.AgendaIAResponseValues = payload;
    },
    SetEventoIAResponseValues(
      state: ISheetState,
      { payload }: PayloadAction<PerguntarIAEventoResponse | null>
    ) {
      state.EventoIAResponseValues = payload;
    },
    SetEventoIAStreamState(
      state: ISheetState,
      { payload }: PayloadAction<EventoIAStreamState | null>
    ) {
      state.EventoIAStreamState = payload;
    },
    SetAgendaIAStreamState(
      state: ISheetState,
      { payload }: PayloadAction<AgendaIAStreamState | null>
    ) {
      state.AgendaIAStreamState = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(SetEventoId, (state) => {
      state.CheckinEquipanteId = null;
      state.CheckinParticipanteId = null;
      state.CheckinCasalParticipanteId = null;
      state.CheckinCasaslEquipanteId = null;
    });
    builder.addCase(SetFormMinisterioValues, (state, action) => {
      state.FormMinisterioValues = action.payload;
    });
  },
});

export const {
  SetProfileCardParticipanteId,
  SetProfileCardParticipanteEventoId,
  SetProfileCardEquipanteId,
  SetProfileCardEquipanteEventoId,
  SetProfileCardMinisterioId,
  SetProfileCardPedidoOracaoId,
  SetProfileCardAgendaId,
  SetPageTitle,
  SetProfileCardAccordionOpen,
  SetPagamentosParticipanteId,
  SetPagamentosEquipanteId,
  SetPagamentosLoginId,
  SetLancamentosAbertoEmail,
  SetPagamentosRealizadosEmail,
  SetTurmasEmail,
  SetTurmasTipo,
  SetTurmasPassadas,
  SetDetalheTurmaId,
  SetDetalheTurmaTipo,
  SetMensagensEquipanteId,
  SetMensagensInfantilContexto,
  SetAnexosEquipanteId,
  SetMembrosEquipeId,
  SetAdicionaisEventoId,
  SetCupomUtilizacoesContext,
  SetAnexosEquipeId,
  SetMovimentacaoProdutoId,
  SetAnexosLancamentoId,
  SetAnexosParticipanteId,
  SetAnexosTarefaId,
  SetMensagensParticipanteId,
  SetHistoricoContext,
  SetCallbackWhatsapp,
  SetWhatsappConnectOpen,
  SetFormCirculoValues,
  SetFormCaronaValues,
  SetFormReuniaoValues,
  SetFormCamposValues,
  SetFormCamposPersonalizadosValues,
  SetFormMensagensValues,
  SetFormFormularioValues,
  SetIntegracoesConfiguracaoValues,
  SetFormProdutoValues,
  SetFormCredencialValues,
  SetFormPagamentoValues,
  SetFormPadrinhoValues,
  SetDirigentesCirculoId,
  SetProfessoresTurmaId,
  SetFormQuartoValues,
  SetFormParticipanteId,
  SetFormEquipanteId,
  SetFormEquipanteContext,
  SetFormAdicionalValues,
  SetFormCupomValues,
  SetCheckinParticipanteId,
  SetCheckinCasalParticipanteId,
  SetCheckinCasaslEquipanteId,
  SetCheckinEquipanteId,
  SetFormEventoValues,
  SetPainelEventoContext,
  SetFormPequenosGruposValues,
  SetFormDisciplinasValues,
  SetFormTurmaValues,
  SetShareEventoValues,
  SetShareCursoValues,
  SetSharePequenosGruposValues,
  SetShareInformativoValues,
  SetFormMarcadorValues,
  SetFormLocalValues,
  SetFormSemestreLetivoValues,
  SetFormEquipeValues,
  SetFormAgendaValues,
  SetFormInformativoValues,
  SetFormMensagemValues,
  SetFormFormMidiaValues,
  SetFormEquipesValues,
  SetFormInviteValues,
  SetConviteResponsavelSalinhaContext,
  SetFormPermissaoValues,
  SetCredencialPessoa,
  SetGlobalDialogProps,
  SetSheetSuppressOutsideClick,
  SetFormMinisterioValues,
  SetHideEvento,
  SetFormProvider,
  setSelectedVenda,
  setSelectedTarefa,
  SetFormLinkPageValues,
  SetFormLinkValues,
  SetFormLinkTemplateValues,
  SetFormEventoBackgroundValues,
  SetFormJornadaValues,
  SetImpressaoSheet,
  SetAvaliacaoViewValues,
  SetAvaliacaoSheetValues,
  SetFormGrupoDevocionalValues,
  SetFormCheckinValues,
  SetFormEtiquetaDevocionalValues,
  SetSelectedCheckinId,
  SetSelectedGrupoDevocionalId,
  SetRetrospectiva2025Open,
  SetFormTipoAgendaValues,
  SetFormTipoAgendaCamposValues,
  SetFormAgendaFuncaoValues,
  SetSelectedAgendaId,
  SetAgendaEscalasOpen,
  SetGuiaUsuariosAgendaOpen,
  SetFormGuiaUsuariosAgendaValues,
  SetAgendaInformacoesAdicionaisOpen,
  SetIncluirVoluntarioOpen,
  SetAssistenteMontagemOpen,
  SetConfigMontagemEquipesOpen,
  SetAgendaIAResponseValues,
  SetEventoIAResponseValues,
  SetEventoIAStreamState,
  SetAgendaIAStreamState,
} = sheetSlice.actions;
export const sheetReducer = sheetSlice.reducer;

// Seletor base para o slice
const selectSheetState = (state: RootState) => state.sheetReducer;

// Seletores memoizados com createSelector para otimização de performance
export const sheetSelectors = {
  // Seletores básicos (mantidos por compatibilidade)
  FormMinisterioValues: (state: RootState) =>
    state.sheetReducer.FormMinisterioValues,
  FormProvider: (state: RootState) => state.sheetReducer.FormProvider,
  FormProdutoValuesSelector: (state: RootState) =>
    state.sheetReducer.FormProdutoValues,
  SelectedVendaSelector: (state: RootState) => state.sheetReducer.SelectedVenda,
  SelectedTarefaSelector: (state: RootState) =>
    state.sheetReducer.SelectedTarefa,
  FormGrupoDevocionalValues: (state: RootState) =>
    state.sheetReducer.FormGrupoDevocionalValues,
  FormCheckinValues: (state: RootState) => state.sheetReducer.FormCheckinValues,
  FormEtiquetaDevocionalValues: (state: RootState) =>
    state.sheetReducer.FormEtiquetaDevocionalValues,
  SelectedCheckinId: (state: RootState) => state.sheetReducer.SelectedCheckinId,
  SelectedGrupoDevocionalId: (state: RootState) =>
    state.sheetReducer.SelectedGrupoDevocionalId,

  // NOVOS seletores memoizados para otimização
  ProfileCardIds: createSelector(
    [selectSheetState],
    (sheet) => ({
      ProfileCardParticipanteId: sheet.ProfileCardParticipanteId,
      ProfileCardParticipanteEventoId: sheet.ProfileCardParticipanteEventoId,
      ProfileCardEquipanteId: sheet.ProfileCardEquipanteId,
      ProfileCardEquipanteEventoId: sheet.ProfileCardEquipanteEventoId,
      ProfileCardMinisterioId: sheet.ProfileCardMinisterioId,
      ProfileCardPedidoOracaoId: sheet.ProfileCardPedidoOracaoId,
      ProfileCardAgendaId: sheet.ProfileCardAgendaId,
    })
  ),

  CheckinIds: createSelector(
    [selectSheetState],
    (sheet) => ({
      CheckinParticipanteId: sheet.CheckinParticipanteId,
      CheckinEquipanteId: sheet.CheckinEquipanteId,
      CheckinCasalParticipanteId: sheet.CheckinCasalParticipanteId,
      CheckinCasaslEquipanteId: sheet.CheckinCasaslEquipanteId,
    })
  ),

  PagamentosIds: createSelector(
    [selectSheetState],
    (sheet) => ({
      PagamentosParticipanteId: sheet.PagamentosParticipanteId,
      PagamentosEquipanteId: sheet.PagamentosEquipanteId,
      PagamentosLoginId: sheet.PagamentosLoginId,
    })
  ),

  MensagensIds: createSelector(
    [selectSheetState],
    (sheet) => ({
      MensagensEquipanteId: sheet.MensagensEquipanteId,
      MensagensParticipanteId: sheet.MensagensParticipanteId,
    })
  ),

  AnexosIds: createSelector(
    [selectSheetState],
    (sheet) => ({
      AnexosLancamentoId: sheet.AnexosLancamentoId,
      AnexosParticipanteId: sheet.AnexosParticipanteId,
      AnexosEquipeId: sheet.AnexosEquipeId,
      AnexosEquipanteId: sheet.AnexosEquipanteId,
      AnexosTarefaId: sheet.AnexosTarefaId,
    })
  ),

  FormEventoValues: createSelector(
    [selectSheetState],
    (sheet) => ({
      FormEventoValues: sheet.FormEventoValues,
      FormPequenosGruposValues: sheet.FormPequenosGruposValues,
      FormDisciplinasValues: sheet.FormDisciplinasValues,
      FormTurmaValues: sheet.FormTurmaValues,
    })
  ),

  FormCadastroValues: createSelector(
    [selectSheetState],
    (sheet) => ({
      FormProdutoValues: sheet.FormProdutoValues,
      FormCirculoValues: sheet.FormCirculoValues,
      FormCaronaValues: sheet.FormCaronaValues,
      FormPadrinhoValues: sheet.FormPadrinhoValues,
      FormQuartoValues: sheet.FormQuartoValues,
      FormMarcadorValues: sheet.FormMarcadorValues,
      FormLocalValues: sheet.FormLocalValues,
      FormSemestreLetivoValues: sheet.FormSemestreLetivoValues,
      FormEquipeValues: sheet.FormEquipeValues,
      FormAgendaValues: sheet.FormAgendaValues,
    })
  ),

  FormConfigValues: createSelector(
    [selectSheetState],
    (sheet) => ({
      FormMidiaValues: sheet.FormMidiaValues,
      FormEquipesValues: sheet.FormEquipesValues,
      FormMensagensValues: sheet.FormMensagensValues,
      FormCamposValues: sheet.FormCamposValues,
      FormFormularioValues: sheet.FormFormularioValues,
      FormCamposPersonalizadosValues: sheet.FormCamposPersonalizadosValues,
      FormCredencialValues: sheet.FormCredencialValues,
    })
  ),

  ShareValues: createSelector(
    [selectSheetState],
    (sheet) => ({
      ShareEventoValues: sheet.ShareEventoValues,
      ShareCursoValues: sheet.ShareCursoValues,
      SharePequenosGruposValues: sheet.SharePequenosGruposValues,
      ShareInformativoValues: sheet.ShareInformativoValues,
    })
  ),

  AgendaState: createSelector(
    [selectSheetState],
    (sheet) => ({
      SelectedAgendaId: sheet.SelectedAgendaId,
      AgendaEscalasOpen: sheet.AgendaEscalasOpen,
      GuiaUsuariosAgendaOpen: sheet.GuiaUsuariosAgendaOpen,
      FormGuiaUsuariosAgendaValues: sheet.FormGuiaUsuariosAgendaValues,
      AgendaInformacoesAdicionaisOpen: sheet.AgendaInformacoesAdicionaisOpen,
      IncluirVoluntarioOpen: sheet.IncluirVoluntarioOpen,
      AgendaIAResponseValues: sheet.AgendaIAResponseValues,
      AgendaIAStreamState: sheet.AgendaIAStreamState,
    })
  ),

  EventoIAState: createSelector(
    [selectSheetState],
    (sheet) => ({
      EventoIAResponseValues: sheet.EventoIAResponseValues,
      EventoIAStreamState: sheet.EventoIAStreamState,
    })
  ),

  GlobalDialogState: createSelector(
    [selectSheetState],
    (sheet) => sheet.GlobalDialog
  ),

  ImpressaoSheetState: createSelector(
    [selectSheetState],
    (sheet) => sheet.ImpressaoSheet
  ),
};
